import React, { useEffect, useMemo, useState } from 'react';
import { http_get } from '../utils/api_client';
import { API_ENDPOINTS } from '../utils/endpoints';

// BL-084 — "Add item" picker. Three tabs backed by the shared learning-materials
// list endpoint (category=paths / walkthrough / docs). Selecting a row adds it to
// the checklist; already-added materials are shown disabled.
const TABS = [
  { key: 'paths', label: 'Paths', category: 'paths', ic: 'path' },
  { key: 'walkthrough', label: 'Walkthroughs', category: 'walkthroughs', ic: 'walk' }
  // Knowledge bases (material_type 'docs') are intentionally hidden until the KB
  // feature is officially re-enabled in the app. The item-type styling and the
  // 'kb' typeMeta mapping stay in place, so restoring this is a one-line change:
  //   { key: 'docs', label: 'Knowledge bases', category: 'docs', ic: 'kb' }
];

const AddItemPicker = ({ excludeIds = new Set(), onAdd, onClose }) => {
  const [tab, setTab] = useState('paths');
  const [cache, setCache] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const active = TABS.find((t) => t.key === tab) || TABS[0];

  useEffect(() => {
    if (cache[tab]) return;
    let cancelled = false;
    setLoading(true);
    http_get(API_ENDPOINTS.GET_LEARNING_MATERIALS, { category: active.category })
      .then(({ data }) => { if (!cancelled) setCache((c) => ({ ...c, [tab]: data || [] })); })
      .catch(() => { if (!cancelled) setCache((c) => ({ ...c, [tab]: [] })); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const rows = useMemo(() => {
    const list = cache[tab] || [];
    const q = search.trim().toLowerCase();
    return q ? list.filter((m) => String(m.name || '').toLowerCase().includes(q)) : list;
  }, [cache, tab, search]);

  return (
    <div className="cl-picker-overlay" onClick={onClose}>
      <div className="cl-picker" onClick={(e) => e.stopPropagation()}>
        <div className="cl-picker__h">
          <div className="cl-picker__title">Add item</div>
          <span className="cl-picker__x" onClick={onClose}>✕</span>
        </div>

        <div className="cl-picker__tabs">
          {TABS.map((t) => (
            <span key={t.key} className={`cl-picker__tab ${tab === t.key ? 'on' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
            </span>
          ))}
        </div>

        <div className="cl-picker__search">
          <input placeholder={`Search ${active.label.toLowerCase()}…`} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <div className="cl-picker__list">
          {loading ? (
            <div className="cl-picker__empty">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="cl-picker__empty">Nothing to add here yet.</div>
          ) : rows.map((m) => {
            const added = excludeIds.has(m.id);
            return (
              <div
                key={m.id}
                className={`cl-picker__row ${added ? 'is-added' : ''}`}
                onClick={() => { if (!added) { onAdd(m); } }}
              >
                <span className={`cl-ic ${active.ic}`} aria-hidden>{active.label[0]}</span>
                <div className="cl-picker__rowbody">
                  <div className="nm">{m.name}</div>
                </div>
                <span className="cl-picker__add">{added ? 'Added' : '+ Add'}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AddItemPicker;
